import type { RefObject } from 'react';
import {
  POPOVER_MIN_HEIGHT,
  POPOVER_MIN_WIDTH,
  POPOVER_VIEWPORT_PADDING,
} from './Cmarkdowntable.constants';
import { clampNumber } from './Cmarkdowntable.helpers';
import type {
  CmarkdowntablePopoverRect,
  PopoverDragState,
  PopoverResizeState,
  TablePopoverPointerHandler,
} from './Cmarkdowntable.types';

const BUTTON_SELECTOR = 'button, [role="button"]';

interface ViewportSize {
  width: number;
  height: number;
}

interface PinPopoverRectInput {
  popoverRef: RefObject<HTMLDivElement | null>;
  applyManualRect: (rect: CmarkdowntablePopoverRect | null) => void;
}

interface PointerStartInput {
  event: Parameters<TablePopoverPointerHandler>[0];
  pinned: CmarkdowntablePopoverRect;
}

interface MatchingPointerStateInput<TState extends { pointerId: number }> {
  stateRef: RefObject<TState | null>;
  pointerId: number;
}

interface DraggedRectInput {
  drag: PopoverDragState;
  pointerX: number;
  pointerY: number;
  viewport: ViewportSize;
}

interface ResizedRectInput {
  resize: PopoverResizeState;
  pointerX: number;
  pointerY: number;
  viewport: ViewportSize;
}

export function pinPopoverRect(input: PinPopoverRectInput): CmarkdowntablePopoverRect | null {
  const panelElement = input.popoverRef.current;
  if (!panelElement) {
    return null;
  }

  const rect = panelElement.getBoundingClientRect();
  const pinned = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  input.applyManualRect(pinned);
  return pinned;
}

export function isPrimaryPointerStart(event: Parameters<TablePopoverPointerHandler>[0]): boolean {
  return event.button === 0;
}

export function isButtonTarget(targetElement: EventTarget): boolean {
  return targetElement instanceof Element && targetElement.closest(BUTTON_SELECTOR) != null;
}

export function buildDragState(input: PointerStartInput): PopoverDragState {
  return {
    pointerId: input.event.pointerId,
    startPointerX: input.event.clientX,
    startPointerY: input.event.clientY,
    startLeft: input.pinned.left,
    startTop: input.pinned.top,
    width: input.pinned.width,
    height: input.pinned.height,
  };
}

export function buildResizeState(input: PointerStartInput): PopoverResizeState {
  return {
    pointerId: input.event.pointerId,
    startPointerX: input.event.clientX,
    startPointerY: input.event.clientY,
    startWidth: input.pinned.width,
    startHeight: input.pinned.height,
    left: input.pinned.left,
    top: input.pinned.top,
  };
}

export function getMatchingPointerState<TState extends { pointerId: number }>(
  input: MatchingPointerStateInput<TState>,
): TState | null {
  const state = input.stateRef.current;
  if (!state || state.pointerId !== input.pointerId) {
    return null;
  }

  return state;
}

export function capturePointer(event: Parameters<TablePopoverPointerHandler>[0]): void {
  event.currentTarget.setPointerCapture(event.pointerId);
}

export function releasePointerIfCaptured(event: Parameters<TablePopoverPointerHandler>[0]): void {
  if (event.currentTarget.hasPointerCapture(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
}

export function readViewportSize(): ViewportSize {
  return {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  };
}

export function resolveDraggedRect(input: DraggedRectInput): CmarkdowntablePopoverRect {
  return {
    left: clampNumber({
      value: input.drag.startLeft + (input.pointerX - input.drag.startPointerX),
      min: POPOVER_VIEWPORT_PADDING,
      max: input.viewport.width - input.drag.width - POPOVER_VIEWPORT_PADDING,
    }),
    top: clampNumber({
      value: input.drag.startTop + (input.pointerY - input.drag.startPointerY),
      min: POPOVER_VIEWPORT_PADDING,
      max: input.viewport.height - input.drag.height - POPOVER_VIEWPORT_PADDING,
    }),
    width: input.drag.width,
    height: input.drag.height,
  };
}

export function resolveResizedRect(input: ResizedRectInput): CmarkdowntablePopoverRect {
  return {
    left: input.resize.left,
    top: input.resize.top,
    width: clampNumber({
      value: input.resize.startWidth + (input.pointerX - input.resize.startPointerX),
      min: POPOVER_MIN_WIDTH,
      max: input.viewport.width - input.resize.left - POPOVER_VIEWPORT_PADDING,
    }),
    height: clampNumber({
      value: input.resize.startHeight + (input.pointerY - input.resize.startPointerY),
      min: POPOVER_MIN_HEIGHT,
      max: input.viewport.height - input.resize.top - POPOVER_VIEWPORT_PADDING,
    }),
  };
}
